import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Divider,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { compose } from "recompose";
import CustomTextField from "../DriverPopUp/common/CustomTextField";
import Select from "../../../../common/StyledSelect";
import { getDataWithDeduplication } from "../../../../../api/componentAPIs";
import { getEquipmentStatusesWithAllowedDispatch, getTractorError, getDollyError } from "../DispatchPanel/utils";
import { getEquipmentSuffix } from "../../utils/utils";
import { sortBy } from "lodash";
import { inject, observer } from "mobx-react";
import { ONLY_NUMBER_REGEX } from "../constRegex";
const styles = (theme) => ({
  dialogContainer: {
    userSelect: "none",
    backgroundColor: theme.ModalBGColor,
    maxWidth: '362px',
    width: "40%",
    display: "flex",
  },
  dialogTitle: {
    fontSize: theme.headerTextFontSize,
    fontWeight: 600,
    textAlign: "left",
    display: "flex",
    paddingBottom: "inherit",
    flexDirection: "column",
    color: theme.HeaderSelectedText,
  },
  dialogWrapper: {},
  divider: {
    borderBottom: `1px solid ${theme.gridIconBorderColor}`,
    width: "100%",
    marginBottom: 11,
    opacity: "0.7",
    marginTop: 24,
  },
  dialogActionStyle: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 24 12 24",
    margin: 0
  },
  dialogButtonStyle: {
    fontSize: theme.headerTextFontSize,
    display: "flex",
    textTransform: "none",
    borderRadius: "2px",
    float: "right",
    marginLeft: 12
  },
  headerLabel: {
    color: theme.HeaderSelectedText,
    fontSize: theme.headerTextFontSize,
    fontWeight: 600,
  },
  selectLabel: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    opacity: 0.6,
    alignItems: "left",
    marginTop: 10,
  },
  actionButton: {
    display: "flex",
    justifyContent: "flex-end",
    margin: 0,
  },
  selectRoot: {
    marginTop: 8,
    marginBottom: 8,
  },
  trailerNumber: {
    marginLeft: 30,
  },
  containerLabel: {
    display: "flex",
  },
});
@inject("DispatchStore")
@observer
class StartTrip extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      odometerReading: 0,
      tractorList: [],
      dollyList: [],
      selectedTractor: null,
      selectedDollies: {}, // Store dolly per routeId: { routeId1: dollyOption1, routeId2: dollyOption2 }
      isLoading: true,
      tractorError: null,
      dollyErrors: {}, // Store error per routeId
    };
  }

  componentDidMount() {
    this.fetchEquipment();
  }

  fetchEquipment = () => {
    const { userData, driverInfo, configJSON, DispatchStore } = this.props;
    const showTrailerScac = configJSON?.showTrailerScac ?? false;

    getDataWithDeduplication("GetAllEquipment", `terminalCode=${userData.userProfile.terminal.code}`).then(
      (response) => {
        if (response?.status !== 200) {
          this.setState({ isLoading: false });
          return;
        }

        const allEquipment = response?.data ?? {};
        const { tractorStatusConfig = [], dollyStatusConfig = [] } =
          DispatchStore.staticClientData?.clientCodes ?? {};

        const allowedTractorStatuses = getEquipmentStatusesWithAllowedDispatch(tractorStatusConfig);
        const allowedDollyStatuses = getEquipmentStatusesWithAllowedDispatch(dollyStatusConfig);

        // Helper to create equipment suffix
        const createSuffix = (status, scac) => getEquipmentSuffix({
          equipmentStatus: status,
          equipmentScac: scac,
          showTrailerScac,
        });

        // Map and filter tractors
        const tractorList = this.sortByLabel(
          (allEquipment.tractor ?? [])
            .filter((item) => allowedTractorStatuses.includes(item.tractorStatus))
            .map(({ tractorNumber, tractorStatus, tractorScac, tractorId, isStraightTruck, defects }) => ({
              label: tractorNumber,
              value: tractorId,
              labelSuffix: createSuffix(tractorStatus, tractorScac),
              tractorStatus,
              isStraightTruck,
              ...(tractorScac && { tractorScac }),
              defects,
            }))
        );

        // Map all dollies for pre-selection lookup
        const allDollyOptions = (allEquipment.dolly ?? []).map(
          ({ dollyNumber, dollyStatus, dollyId, defects, dollyScac }) => ({
            label: dollyNumber,
            value: dollyId,
            labelSuffix: createSuffix(dollyStatus, dollyScac),
            dollyStatus,
            defects,
          })
        );

        // Filter dollies by allowed statuses
        const dollyList = this.sortByLabel(
          allDollyOptions.filter((item) => allowedDollyStatuses.includes(item.dollyStatus))
        );

        // Pre-select tractor from driverInfo
        const preSelectedTractor = this.findEquipmentByLabel(tractorList, driverInfo?.tractorNumber);

        // Pre-select dollies from dispatchRoutes (routes after the first one require dollies)
        const preSelectedDollies = this.getPreSelectedDollies(driverInfo?.dispatchRoutes, dollyList, allDollyOptions);

        this.setState({
          tractorList,
          dollyList,
          selectedTractor: preSelectedTractor,
          selectedDollies: preSelectedDollies,
          isLoading: false,
        });
      }
    );
  };

  findEquipmentByLabel = (equipmentList, label) => {
    if (!label) return null;
    return equipmentList.find(
      (item) => item.label?.toLowerCase() === label.toLowerCase()
    ) ?? null;
  };

  getPreSelectedDollies = (dispatchRoutes, dollyList, allDollyOptions) => {
    const preSelectedDollies = {};
    const routesRequiringDolly = dispatchRoutes?.slice(1) || [];

    routesRequiringDolly.forEach((route) => {
      if (!route.dollyNumber) return;

      const routeId = route.routeId || route.routeName;
      let matchedDolly = this.findEquipmentByLabel(dollyList, route.dollyNumber);

      // If not in filtered list, search all dollies and add to dropdown
      if (!matchedDolly) {
        matchedDolly = this.findEquipmentByLabel(allDollyOptions, route.dollyNumber);
        if (matchedDolly) {
          dollyList.push(matchedDolly);
        }
      }

      if (matchedDolly) {
        preSelectedDollies[routeId] = matchedDolly;
      }
    });

    return preSelectedDollies;
  };

  sortByLabel = (optionsList = []) => {
    return sortBy(optionsList, [(item) => parseInt(item.label)]);
  };

  handleTractorChange = (selectedOption) => {
    const { tractorStatusConfig = [] } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};
    const tractorError = getTractorError({
      tractor: selectedOption,
      tractorStatusConfig,
      isStraightTruckTrailer: false,
      isPreDispatch: false,
    });
    this.setState({ selectedTractor: selectedOption, tractorError });
  };

  handleDollyChange = (routeId, selectedOption) => {
    const { dollyStatusConfig = [] } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};
    const dollyError = getDollyError({
      dolly: selectedOption,
      trailersData: [], // No duplicate check needed here
      dollyStatusConfig,
      isPreDispatch: false,
    });
    this.setState((prevState) => ({
      selectedDollies: {
        ...prevState.selectedDollies,
        [routeId]: selectedOption,
      },
      dollyErrors: {
        ...prevState.dollyErrors,
        [routeId]: dollyError,
      },
    }));
  };

  handleOdometerChange = (event) => {
    const value = event.target.value;
    this.setState({ odometerReading: value });
  };

  onCancel = () => {
    this.props.closeStartTripModal();
  };

  isValidStartTrip = () => {
    const { driverInfo } = this.props;
    const { selectedTractor, selectedDollies } = this.state;
    const { tractorStatusConfig = [], dollyStatusConfig = [] } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};

    // Validate tractor
    const tractorError = getTractorError({
      tractor: selectedTractor,
      tractorStatusConfig,
      isStraightTruckTrailer: false,
      isPreDispatch: false,
    });

    // Validate all dollies for routes beyond the first one
    const dollyErrors = {};
    const routesRequiringDolly = driverInfo.dispatchRoutes?.slice(1) || [];
    routesRequiringDolly.forEach((route) => {
      const routeId = route.routeId || route.routeName;
      const dolly = selectedDollies[routeId];
      const dollyError = getDollyError({
        dolly,
        trailersData: [],
        dollyStatusConfig,
        isPreDispatch: false,
      });
      if (dollyError) {
        dollyErrors[routeId] = dollyError;
      }
    });

    this.setState({ tractorError, dollyErrors });

    const hasErrors = tractorError || Object.keys(dollyErrors).length > 0;
    return !hasErrors;
  };

  createStartTripRequest = () => {
    if (!this.isValidStartTrip()) {
      return;
    }
    const { driverInfo, startTrip } = this.props;
    const { odometerReading, selectedTractor, selectedDollies } = this.state;
    const startTripRequest = {
      driverId: driverInfo.driverId,
      dispatchId: driverInfo.dispatchId,
      terminalCode: this.props.userData.userProfile.terminal.code,
      routeId: driverInfo.dispatchRoutes[0].routeId,
      trailerNumber: driverInfo.dispatchRoutes[0].trailerNumber,
      manifestNumber: driverInfo.dispatchRoutes[0].manifestNumber,
      odometerReading: odometerReading,
      tractorNumber: selectedTractor?.label,
      tractorScac: selectedTractor?.tractorScac,
      routes: Object.entries(selectedDollies).map(([routeId, dolly]) => ({ routeId, dollyId: dolly?.value })),
    };
    startTrip(startTripRequest);
  };
  render() {
    const { classes, driverInfo } = this.props;
    const { odometerReading, tractorList, dollyList, selectedTractor, selectedDollies, tractorError, dollyErrors } = this.state;

    return (
      <Dialog
        maxWidth="lg"
        open={true}
        onClose={this.onCancel}
        classes={{ paper: classes.dialogContainer }}
        className={classes.dialogWrapper}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          Start Trip
          <Divider className={classes.divider} />
        </DialogTitle>
        <DialogContent className="customScroll">
          <div className={classes.headerLabel}>{driverInfo.driverName} #{driverInfo.driverNumber}</div>
          <div className={classes.selectLabel}>EQUIPMENT DETAILS </div>
          <Select
            textFieldProps={{
              id: "",
              label: "SELECT TRACTOR",
              variant: "outlined",
              helperText: tractorError || "",
              className: classes.selectField,
            }}
            rootClass={classes.selectRoot}
            name=""
            error={Boolean(tractorError)}
            value={selectedTractor?.value ?? ""}
            onChange={this.handleTractorChange}
            data={tractorList}
          />
          {driverInfo.dispatchRoutes?.length >= 1 && driverInfo.dispatchRoutes.map((item, index) => {
            const routeId = item.routeId || item.routeName;
            const selectedDolly = selectedDollies[routeId] ?? null;
            return (
              <React.Fragment key={routeId || index}>
                <div className={classes.containerLabel}>
                  <div className={classes.selectLabel}> {`Route Name ${index + 1}:`} <b>{item.routeName}</b></div>
                  <div className={classes.selectLabel + " " + classes.trailerNumber}> {`Trailer Number ${index + 1}:`} <b>{item.trailerNumber}</b></div>
                </div>
                {index > 0 && (
                  <Select
                    textFieldProps={{
                      id: "",
                      label: "SELECT DOLLY",
                      variant: "outlined",
                      helperText: dollyErrors[routeId] || "",
                    }}
                    rootClass={classes.selectRoot}
                    name=""
                    error={Boolean(dollyErrors[routeId])}
                    value={selectedDolly?.value ?? ""}
                    onChange={(selectedOption) => this.handleDollyChange(routeId, selectedOption)}
                    data={dollyList}
                  />
                )}
              </React.Fragment>
            );
          })}
          <CustomTextField
            id="ODOMETER"
            label="ODOMETER (OPTIONAL)"
            value={odometerReading}
            onChange={this.handleOdometerChange}
            inputProps={{
              pattern: ONLY_NUMBER_REGEX,
            }}
          />
        </DialogContent>
        <DialogActions classes={{ root: classes.dialogActionStyle }}>
          <div></div>
          <div className={classes.actionButton}>
            <Button
              variant="outlined"
              onClick={this.onCancel}
              color="primary"
              className={classes.dialogButtonStyle}
            >
              Cancel
            </Button>
            <Button
              autoFocus
              variant="contained"
              onClick={this.createStartTripRequest}
              color="primary"
              disabled={Boolean(tractorError) || Object.values(dollyErrors || {}).some(Boolean)}
              className={classes.dialogButtonStyle}
            >
              Start Trip
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    );
  }
}

export default compose(withStyles(styles))(StartTrip);
